import React, { useState, useEffect, useMemo } from 'react';
import AdminLeftMenu from './backend/AdminLeftMenu';
import { useOutletContext, Link } from "react-router-dom";
import { useAuth } from '../contexts/AuthContext';
import FacilityService from '../services/facilityService';
import toaster from '../helpers/toaster';
import { API_BASE_URL } from '../config/constants';

const FacilitySearch = () => {
    const [userData] = useOutletContext(); // eslint-disable-line no-unused-vars
    const { currentUser } = useAuth();
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedState, setSelectedState] = useState('');
    const [facilityNameFilter, setFacilityNameFilter] = useState('');
    const [requestedFacilities, setRequestedFacilities] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [requestingFacilities, setRequestingFacilities] = useState(new Set());
    
    const facilityService = useMemo(() => new FacilityService(), []);

    // Load user's facility requests on component mount
    useEffect(() => {
        const loadUserFacilityRequests = async () => {
            if (currentUser?.uid) {
                try {
                    const requests = await facilityService.getUserFacilityRequests(currentUser.uid);
                    setRequestedFacilities(requests);
                } catch (error) {
                    console.error('Error loading facility requests:', error);
                }
            }
        };
        
        loadUserFacilityRequests();
    }, [currentUser, facilityService]);

    // Removed searchTypes - now using direct state selection (updated)

    const usStates = [
        { value: 'AL', label: 'Alabama' },
        { value: 'AK', label: 'Alaska' },
        { value: 'AZ', label: 'Arizona' },
        { value: 'AR', label: 'Arkansas' },
        { value: 'CA', label: 'California' },
        { value: 'CO', label: 'Colorado' },
        { value: 'CT', label: 'Connecticut' },
        { value: 'DE', label: 'Delaware' },
        { value: 'FL', label: 'Florida' },
        { value: 'GA', label: 'Georgia' },
        { value: 'HI', label: 'Hawaii' },
        { value: 'ID', label: 'Idaho' },
        { value: 'IL', label: 'Illinois' },
        { value: 'IN', label: 'Indiana' },
        { value: 'IA', label: 'Iowa' },
        { value: 'KS', label: 'Kansas' },
        { value: 'KY', label: 'Kentucky' },
        { value: 'LA', label: 'Louisiana' },
        { value: 'ME', label: 'Maine' },
        { value: 'MD', label: 'Maryland' },
        { value: 'MA', label: 'Massachusetts' },
        { value: 'MI', label: 'Michigan' },
        { value: 'MN', label: 'Minnesota' },
        { value: 'MS', label: 'Mississippi' },
        { value: 'MO', label: 'Missouri' },
        { value: 'MT', label: 'Montana' },
        { value: 'NE', label: 'Nebraska' },
        { value: 'NV', label: 'Nevada' },
        { value: 'NH', label: 'New Hampshire' },
        { value: 'NJ', label: 'New Jersey' },
        { value: 'NM', label: 'New Mexico' },
        { value: 'NY', label: 'New York' },
        { value: 'NC', label: 'North Carolina' },
        { value: 'ND', label: 'North Dakota' },
        { value: 'OH', label: 'Ohio' },
        { value: 'OK', label: 'Oklahoma' },
        { value: 'OR', label: 'Oregon' },
        { value: 'PA', label: 'Pennsylvania' },
        { value: 'RI', label: 'Rhode Island' },
        { value: 'SC', label: 'South Carolina' },
        { value: 'SD', label: 'South Dakota' },
        { value: 'TN', label: 'Tennessee' },
        { value: 'TX', label: 'Texas' },
        { value: 'UT', label: 'Utah' },
        { value: 'VT', label: 'Vermont' },
        { value: 'VA', label: 'Virginia' },
        { value: 'WA', label: 'Washington' },
        { value: 'WV', label: 'West Virginia' },
        { value: 'WI', label: 'Wisconsin' },
        { value: 'WY', label: 'Wyoming' },
        { value: 'DC', label: 'District of Columbia' }
    ];

    // Facility types for future use
    // const facilityTypes = [
    //     { value: '1', label: 'Hospital' },
    //     { value: '2', label: 'Clinic' },
    //     { value: '3', label: 'Laboratory' },
    //     { value: '4', label: 'Pharmacy' },
    //     { value: '5', label: 'Nursing Home' },
    //     { value: '6', label: 'Urgent Care' },
    //     { value: '7', label: 'Surgery Center' }
    // ];

    const searchFacilities = async () => {
        if (!selectedState.trim()) {
            new toaster().errorMessage('Please select a state');
            return;
        }

        setIsLoading(true);
        setSearchResults([]);
        setHasSearched(true);

        try {
            // If facility name filter is empty, search by state only
            // If facility name filter has text, search by name within that state
            const searchType = facilityNameFilter.trim() ? 'name' : 'state';
            const searchValue = facilityNameFilter.trim() || selectedState;
            
            const response = await fetch(`${API_BASE_URL}/api/facilities/search?search_type=${searchType}&search_value=${encodeURIComponent(searchValue)}&limit=500`);
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            
            if (result.entry && result.entry.length > 0) {
                // If searching by name, filter results to only include facilities in the selected state
                let filteredResults = result.entry;
                if (facilityNameFilter.trim()) {
                    filteredResults = result.entry.filter(facility => {
                        const facilityState = facility.resource.address?.[0]?.state;
                        return facilityState === selectedState;
                    });
                }
                
                // Mark facilities that are already requested
                const resultsWithStatus = filteredResults.map(facility => ({
                    ...facility,
                    isRequested: requestedFacilities.some(req => req.fhir_id === facility.resource.id)
                }));
                setSearchResults(resultsWithStatus);
                new toaster().successMessage(`Found ${resultsWithStatus.length} facilities`);
            } else {
                setSearchResults([]);
                new toaster().infoMessage('No facilities found matching your criteria');
            }
        } catch (error) {
            console.error('Search error:', error);
            new toaster().errorMessage('Failed to search facilities. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const requestFacility = async (facility) => {
        if (!currentUser?.uid) {
            new toaster().errorMessage('Please log in to request facilities');
            return;
        }

        try {
            // Set individual facility as loading
            setRequestingFacilities(prev => new Set([...prev, facility.resource.id]));
            const org = facility.resource;
            
            const facilityData = {
                fhir_id: org.id,
                name: org.name || 'Unknown Name',
                type: org.type?.[0]?.coding?.[0]?.display || 'Unknown Type',
                address: org.address?.[0] ? {
                    line: org.address[0].line?.[0] || '',
                    city: org.address[0].city || '',
                    state: org.address[0].state || '',
                    postalCode: org.address[0].postalCode || ''
                } : null,
                phone: org.telecom?.find(t => t.system === 'phone')?.value || null,
                email: org.telecom?.find(t => t.system === 'email')?.value || null,
                npi: org.identifier?.find(id => id.system === 'http://hl7.org/fhir/sid/us-npi')?.value || null,
                user_id: currentUser.uid,
                requested_at: new Date().toISOString(),
                status: 'pending'
            };

            // Save to Firebase
            const result = await facilityService.requestFacility(facilityData);
            
            if (result.success) {
                // Add to local state
                setRequestedFacilities(prev => [...prev, { ...facilityData, id: result.id }]);
                
                // Update search results to show as requested
                const updatedResults = searchResults.map(searchResult => 
                    searchResult.resource.id === org.id 
                        ? { ...searchResult, isRequested: true }
                        : searchResult
                );
                setSearchResults(updatedResults);

                new toaster().successMessage(`Facility request submitted for ${org.name}`);
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error requesting facility:', error);
            new toaster().errorMessage('Failed to request facility. Please try again.');
        } finally {
            // Remove individual facility from loading state
            setRequestingFacilities(prev => {
                const newSet = new Set(prev);
                newSet.delete(facility.resource.id);
                return newSet;
            });
        }
    };

    const formatFacilityData = (facility) => {
        const org = facility.resource;
        return {
            id: org.id,
            name: org.name || 'Unknown Name',
            type: org.type?.[0]?.coding?.[0]?.display || 'Unknown Type',
            address: org.address?.[0] ? 
                `${org.address[0].line?.[0] || ''}, ${org.address[0].city || ''}, ${org.address[0].state || ''} ${org.address[0].postalCode || ''}`.trim() : 
                'Address not available',
            phone: org.telecom?.find(t => t.system === 'phone')?.value || 'Phone not available',
            email: org.telecom?.find(t => t.system === 'email')?.value || 'Email not available',
            website: org.telecom?.find(t => t.system === 'url')?.value || 'Website not available',
            npi: org.identifier?.find(id => id.system === 'http://hl7.org/fhir/sid/us-npi')?.value || 'NPI not available'
        };
    };

    return (
        <>
            <div className="offcanvas offcanvas-start" id="demo">
                <AdminLeftMenu />
            </div>
            <div className="rightpane mynotif">
                <div className="container">
                    <div className='row justify-content-center'>
                        <div className='col-md-8 text-center'>
                            <img src="\images\add-physician-hero.png" width={'323'} alt='facility-search' />
                            <h2>Welcome to the Replinq facility portal. Search and request access to healthcare facilities to expand your coverage network.</h2>
                        </div>
                    </div>

                    <div className='row justify-content-center mt-4'>
                        <div className='col-md-8 text-center'>
                            <div className="row">
                                <div className="col-4 col-md-3">
                                    <select 
                                        className="form-select"
                                        value={selectedState}
                                        onChange={(e) => setSelectedState(e.target.value)}
                                    >
                                        <option value="">Select a state...</option>
                                        {usStates.map(state => (
                                            <option key={state.value} value={state.value}>
                                                {state.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-4 col-md-5">
                                    <input 
                                        type="text"
                                        className="form-control" 
                                        placeholder="Filter by facility name (optional)..."
                                        value={facilityNameFilter}
                                        onChange={(e) => setFacilityNameFilter(e.target.value)}
                                        style={{
                                            height: "36px",
                                            border: "1px solid #ddd",
                                            borderRadius: "0px"
                                        }}
                                    />
                                </div>
                                <div className="col-4 col-md-2">
                                    <button 
                                        className='btn btn-physearch' 
                                        type="button"
                                        onClick={searchFacilities} 
                                        disabled={isLoading || !selectedState.trim()}
                                    >
                                        {isLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Searching...
                                            </>
                                        ) : (
                                            'Search'
                                        )}
                                    </button>
                                </div>
                            </div>
                            
                            {searchResults.map((facility, index) => {
                                const facilityData = formatFacilityData(facility);
                                return (
                                    <div className='row mt-5' key={index}>
                                        <div className='col-md-12'>
                                            <div className="upschedulebox m-0">
                                                <div className="uphdr">
                                                    <div className="dr-name phyname">
                                                        <div className="practitioner-info">
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                <h5 className="practitioner-name mb-0">
                                                                    <b>{facilityData.name}</b>
                                                                    <span className="badge ms-2" style={{fontSize: '0.6em', backgroundColor: '#E07A55', color: 'white'}}>
                                                                        {facilityData.type}
                                                                    </span>
                                                                </h5>
                                                            </div>
                                                            
                                                            {/* NPI hidden for now */}
                                                        </div>
                                                    </div>
                                                    {facility.isRequested ?
                                                        <div className="ms-auto">
                                                            <span className='text-success'>
                                                                <i className='fa fa-check'></i> Requested
                                                            </span>
                                                        </div>
                                                        : requestingFacilities.has(facility.resource.id) ?
                                                        <div className="ms-auto">
                                                            <span className='text-muted'>
                                                                <i className='fa fa-spinner fa-spin'></i> Requesting...
                                                            </span>
                                                        </div>
                                                        :
                                                        <div className="ms-auto">
                                                            <Link to='#' className='a-g-link' onClick={() => requestFacility(facility)}>
                                                                <i className='fa fa-plus'></i> Request Facility
                                                            </Link>
                                                        </div>
                                                    }
                                                </div>
                                                <div className="myphy-boxleft text-start phyaddprfile">
                                                    <div className='row'>
                                                        {/* Address */}
                                                        {facilityData.address && 
                                                         facilityData.address !== 'Address not available' && 
                                                         !facilityData.address.includes('Unknown') && 
                                                         !facilityData.address.includes('00000') && (
                                                            <>
                                                                <div className='col-1 text-end mb-3'><i className="fa fa-map-marker text-muted"></i></div>
                                                                <div className='col-11 mb-3'>{facilityData.address}</div>
                                                            </>
                                                        )}
                                                        
                                                        {/* Phone */}
                                                        {facilityData.phone && 
                                                         facilityData.phone !== 'Phone not available' && 
                                                         facilityData.phone !== '(000) 000-0000' && (
                                                            <>
                                                                <div className='col-1 text-end mb-3'><i className="fa fa-phone text-muted"></i></div>
                                                                <div className='col-11 mb-3'>
                                                                    <a href={`tel:${facilityData.phone}`} className="text-decoration-none">
                                                                        {facilityData.phone}
                                                                    </a>
                                                                </div>
                                                            </>
                                                        )}
                                                        
                                                        {/* Email */}
                                                        {facilityData.email && 
                                                         facilityData.email !== 'Email not available' && (
                                                            <>
                                                                <div className='col-1 text-end mb-3'><i className="fa fa-envelope text-muted"></i></div>
                                                                <div className='col-11 mb-3'>
                                                                    <a href={`mailto:${facilityData.email}`} className="text-decoration-none">
                                                                        {facilityData.email}
                                                                    </a>
                                                                </div>
                                                            </>
                                                        )}
                                                        
                                                        {/* Website */}
                                                        {facilityData.website && 
                                                         facilityData.website !== 'Website not available' && (
                                                            <>
                                                                <div className='col-1 text-end mb-3'><i className="fa fa-globe text-muted"></i></div>
                                                                <div className='col-11 mb-3'>
                                                                    <a href={facilityData.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{wordBreak: 'break-all', fontSize: '0.9em'}}>
                                                                        {facilityData.website}
                                                                    </a>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* No Results Message - only show after search has been performed */}
                            {searchResults.length === 0 && !isLoading && hasSearched && (
                                <div className="row mt-4">
                                    <div className="col-md-12 text-center">
                                        <p className="text-muted">No facilities found matching your search criteria. Try adjusting your search.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FacilitySearch;
